

import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Receipt,
  FileText,
  Tag,
} from "lucide-react";

import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import SearchableDropdown from "../../components/SearchableDropdown";
import { expensesApi, vendorsApi, eventsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { ReportTable } from "../../components/ReportTable";
import { ExportButtons, PresetSelect, type ExportSpec } from "../ReportKit";

// Types
interface Expense {
  id: number;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_category_id?: number | null;
  category?: { id: number; name: string; slug?: string } | null;
  vendor_payment_id?: number | null;
  /** Copy of a vendor payment (or filed under the Vendor Payment category) — counted under vendor payments */
  is_vendor_payment?: boolean;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  event_id?: number;
  event?: { id: number; name: string; start_date?: string; client_name?: string };
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  proof_path?: string;
  status: string;
  created_at?: string;
}

// Vendor payments are mirrored as expenses; they're reported under Vendor Payments, not here
const isVendorPaymentCopy = (e: Expense) =>
  e.is_vendor_payment ?? (e.vendor_payment_id != null || e.category?.slug === 'vendor-payment');

export default function ExpensesReportPage() {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string; is_system?: boolean }[]>([]);
  const [vendors, setVendors] = useState<{ id: number; name: string }[]>([]);
  const [events, setEvents] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    loadData();
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      // Load payment methods
      const pmRes = await mastersApi.paymentMethods.list({ status: 'active' });
      const pmData = pmRes.data?.data || pmRes.data || [];
      if (Array.isArray(pmData)) {
        setPaymentMethods(pmData.map((m: any) => m.name));
      }
    } catch (error) {
      console.error("Failed to load filters:", error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesRes, categoriesRes, vendorsRes, eventsRes] = await Promise.all([
        expensesApi.list(),
        mastersApi.expenseCategories.list(),
        vendorsApi.list(),
        eventsApi.list(),
      ]);
      
      // Handle different response formats
      const expensesData = expensesRes.data?.data || expensesRes.data || [];
      setExpenses(Array.isArray(expensesData) ? expensesData : []);
      
      const categoriesData = categoriesRes.data?.data || categoriesRes.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      
      const vendorsData = vendorsRes.data?.data || vendorsRes.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      
      const eventsData = eventsRes.data?.data || eventsRes.data || [];
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error: any) {
      console.error("Error loading expenses:", error);
      showToast(error?.message || "Failed to load expenses data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Every filter except category; vendor-payment copies are split out below
  const matchingExpenses = useMemo(() => {
    let filtered = Array.isArray(expenses) ? expenses : [];
    
    // Date range filter
    if (fromDate && toDate) {
      filtered = filtered.filter(e => {
        const expenseDate = new Date(e.expense_date);
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return expenseDate >= from && expenseDate <= to;
      });
    }
    
    // Search query filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        (e.title || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.category?.name || '').toLowerCase().includes(q) ||
        (e.vendor?.name || '').toLowerCase().includes(q) ||
        (e.event?.name || '').toLowerCase().includes(q) ||
        (e.reference_number || '').toLowerCase().includes(q)
      );
    }
    
    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(e => String(e.vendor_id) === vendorFilter);
    }
    
    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(e => String(e.event_id) === eventFilter);
    }
    
    // Payment method filter
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(e => e.payment_method === paymentMethodFilter);
    }
    
    return filtered;
  }, [expenses, searchQuery, vendorFilter, eventFilter, paymentMethodFilter, fromDate, toDate]);

  const filteredExpenses = useMemo(() => {
    return matchingExpenses.filter(e => {
      if (isVendorPaymentCopy(e)) return false;
      if (categoryFilter === 'all') return true;
      if (categoryFilter === 'none') return e.expense_category_id == null;
      return String(e.expense_category_id) === categoryFilter;
    });
  }, [matchingExpenses, categoryFilter]);

  const vendorPaymentsTotal = useMemo(() => {
    return matchingExpenses
      .filter(isVendorPaymentCopy)
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [matchingExpenses]);

  // Excel / Print: the rows as filtered on screen, with their total
  const buildExport = (): ExportSpec => {
    const total = filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const nameOf = (list: { id: number; name: string }[], id: string) => list.find((x) => String(x.id) === id)?.name ?? id;
    const filters = [
      categoryFilter !== 'all' && `Category: ${categoryFilter === 'none' ? 'Uncategorised' : nameOf(categories, categoryFilter)}`,
      vendorFilter !== 'all' && `Vendor: ${nameOf(vendors, vendorFilter)}`,
      eventFilter !== 'all' && `Event: ${nameOf(events, eventFilter)}`,
      paymentMethodFilter !== 'all' && `Method: ${paymentMethodFilter}`,
      searchQuery.trim() && `Search: “${searchQuery.trim()}”`,
    ].filter((f): f is string => !!f);
    return {
      title: 'Expenses Report',
      fileName: 'expenses',
      period: fromDate && toDate ? { from: fromDate, to: toDate } : undefined,
      summary: [
        { label: 'Total expenses', value: total, type: 'money' },
        { label: 'Expenses', value: filteredExpenses.length, type: 'number' },
        ...(vendorPaymentsTotal > 0 ? [{ label: 'Vendor payments (not included)', value: vendorPaymentsTotal, type: 'money' as const }] : []),
      ],
      tables: [{
        title: 'Expenses',
        columns: [
          { label: 'Title' }, { label: 'Description' }, { label: 'Category' }, { label: 'Date', type: 'date' },
          { label: 'Vendor' }, { label: 'Event' }, { label: 'Method' }, { label: 'Reference' }, { label: 'Amount', type: 'money' },
        ],
        rows: filteredExpenses.map((e) => [
          e.title, e.description, e.category?.name || 'Uncategorised', e.expense_date?.slice(0, 10),
          e.vendor?.name, e.event?.name, e.payment_method, e.reference_number, Number(e.amount) || 0,
        ]),
        totals: ['Total', null, null, null, null, null, null, null, total],
      }],
      notes: [
        ...(filters.length ? [`Filters: ${filters.join('; ')}`] : []),
        ...(vendorPaymentsTotal > 0 ? [`Vendor payments in this period: ${formatCurrencyFull(vendorPaymentsTotal)} — listed under Vendor Payments, not included above`] : []),
      ],
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Expenses Report</h1>
        <p className="text-sm text-stone-500">View and analyze expense records</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search expenses..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PresetSelect
            from={fromDate}
            to={toDate}
            onPick={(r) => { setFromDate(r.from); setToDate(r.to); }}
            allowAll
          />
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Categories" },
              // The system Vendor Payment category is left out: those rows aren't in this report
              ...categories.filter((cat) => !cat.is_system).map((cat) => ({
                value: String(cat.id),
                label: cat.name,
              })),
              { value: "none", label: "Uncategorised" },
            ]}
            value={categoryFilter}
            onChange={setCategoryFilter}
            placeholder="Filter by category"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Vendors" },
              ...vendors.map((vendor) => ({
                value: String(vendor.id),
                label: vendor.name,
              })),
            ]}
            value={vendorFilter}
            onChange={setVendorFilter}
            placeholder="Filter by vendor"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Events" },
              ...events.map((event) => ({
                value: String(event.id),
                label: event.name,
              })),
            ]}
            value={eventFilter}
            onChange={setEventFilter}
            placeholder="Filter by event"
          />
          <SearchableDropdown
            options={[
              { value: "all", label: "All Methods" },
              ...paymentMethods.map((pm) => ({
                value: pm,
                label: pm,
              })),
            ]}
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
            placeholder="Payment method"
          />
          <ExportButtons build={buildExport} disabled={filteredExpenses.length === 0} />
        </div>
      </div>

      {/* Content */}
      {filteredExpenses.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No expenses found</p>
        </div>
      ) : (
        <ReportTable
          columns={[
            { key: 'title', label: 'Title', width: '25%' },
            { key: 'category', label: 'Category', width: '15%' },
            { key: 'date', label: 'Date', width: '12%' },
            { key: 'vendor', label: 'Vendor/Event', width: '20%' },
            { key: 'method', label: 'Method', width: '13%' },
            { key: 'reference', label: 'Reference', width: '15%' },
            { key: 'amount', label: 'Amount', align: 'right', width: '10%' },
          ]}
          data={filteredExpenses.map(expense => ({
            id: expense.id,
            title: (
              <div>
                <span className="font-medium text-stone-800">{expense.title}</span>
                {expense.description && (
                  <p className="text-xs text-stone-400 truncate max-w-[250px]">{expense.description}</p>
                )}
                {expense.reference_number && (
                  <p className="text-xs text-stone-400">Ref: {expense.reference_number}</p>
                )}
              </div>
            ),
            category: (
              <div className="flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-stone-400" />
                <span>{expense.category?.name || 'Uncategorised'}</span>
              </div>
            ),
            date: formatDate(expense.expense_date),
            vendor: expense.vendor?.name && expense.event?.name ? (
              <div>
                <span>{expense.vendor.name}</span>
                <p className="text-xs text-stone-400">{expense.event.name}</p>
              </div>
            ) : (
              expense.vendor?.name || expense.event?.name || '-'
            ),
            method: <span className="capitalize">{expense.payment_method || '-'}</span>,
            reference: expense.reference_number || '-',
            amount: expense.amount,
          }))}
          totalField="amount"
          formatValue={(value, key) => {
            if (key === 'amount') {
              return <span className="font-bold text-amber-600">{formatCurrencyFull(value)}</span>;
            }
            return value;
          }}
        />
      )}

      {vendorPaymentsTotal > 0 && (
        <p className="mt-3 text-xs text-stone-500">
          Vendor payments in this period: {formatCurrencyFull(vendorPaymentsTotal)} — listed under Vendor Payments, not included above
        </p>
      )}
    </DashboardLayout>
  );
}
